/**
 * Parses case study markdown content into structured sections.
 * Extracts: summary, challenge, approach, result, and testimonial quote.
 */

export interface CaseStudySections {
  summary: string;
  challenge: string;
  approach: string;
  result: string;
  quote?: string;
  quoteAttribution?: string;
}

/**
 * Extracts metadata (client type, services, industry) from the raw
 * content before cleaning — these appear in the top noise as plain text
 * lines between icon images.
 */
export interface CaseStudyMeta {
  clientType?: string;
  services?: string;
  industry?: string;
}

export function extractCaseStudyMeta(raw: string): CaseStudyMeta {
  const lines = raw.split("\n");
  const meta: CaseStudyMeta = {};

  // Look for patterns in the first ~30 lines: icon image followed by label text
  for (let i = 0; i < Math.min(lines.length, 40); i++) {
    const trimmed = lines[i].trim();

    // Client type: after BioTech-icon or similar company icon
    if (/!\[BioTech-icon\]|!\[Medicine-icon\]|!\[MedDev-icon\]|!\[IVD-icon\]/.test(trimmed)) {
      const next = lines[i + 2]?.trim();
      if (next && !next.startsWith("!") && !next.startsWith("[") && !next.startsWith("#") && next.length < 100) {
        meta.clientType = next;
      }
    }

    // Services: after Tag_icon
    if (/!\[Tag_icon\]/.test(trimmed)) {
      const next = lines[i + 2]?.trim();
      if (next && !next.startsWith("!") && !next.startsWith("[") && !next.startsWith("#") && next.length < 200) {
        meta.services = next;
      }
    }

    // Industry: after Pharma & Biotech icon or industry icons
    if (/!\[Pharma.*blue\]|!\[MedDev.*blue\]|!\[IVD.*blue\]/.test(trimmed)) {
      const next = lines[i + 2]?.trim();
      if (next && !next.startsWith("!") && !next.startsWith("[") && !next.startsWith("#") && next.length < 100) {
        meta.industry = next;
      }
    }
  }

  return meta;
}

export function parseCaseStudyContent(cleanedContent: string): CaseStudySections {
  const lines = cleanedContent.split("\n");

  // Find section boundaries by ## headings OR section marker images (![Challenge], ![Approach], ![Result])
  const sectionStarts: { type: string; index: number }[] = [];
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (/^##\s+(Challenge|Desafío|Desafio|Reto|挑战)/i.test(trimmed)) sectionStarts.push({ type: "challenge", index: i });
    else if (/^##\s+(Approach|Enfoque|方法)/i.test(trimmed)) sectionStarts.push({ type: "approach", index: i });
    else if (/^##\s+(Results?|Resultados?|结果)\s*$/i.test(trimmed)) sectionStarts.push({ type: "result", index: i });
    // Detect image-based section markers when no ## heading was found
    else if (/^!\[Challenge\]/i.test(trimmed) && !sectionStarts.some(s => s.type === "challenge")) {
      sectionStarts.push({ type: "challenge", index: i });
    } else if (/^!\[Approach\]/i.test(trimmed) && !sectionStarts.some(s => s.type === "approach")) {
      sectionStarts.push({ type: "approach", index: i });
    } else if (/^!\[Results?\]/i.test(trimmed) && !sectionStarts.some(s => s.type === "result")) {
      sectionStarts.push({ type: "result", index: i });
    }
  }

  // Everything before the first section heading is the summary — but strip HubSpot noise
  const firstSectionIdx = sectionStarts.length > 0 ? sectionStarts[0].index : lines.length;
  const rawSummaryLines = lines.slice(0, firstSectionIdx);

  // Strip noise lines from the summary block: icon images, short labels after icons,
  // CTA links, share blocks, decorative SVGs, etc.
  const cleanSummaryLines = rawSummaryLines.filter((line, i) => {
    const t = line.trim();
    if (!t) return false; // blank
    if (/^!\[/.test(t) || /^\[!\[/.test(t)) return false; // images / linked images
    if (/^\[Read more\]/i.test(t) || /^\[Get in touch\]/i.test(t)) return false; // CTA links
    if (/^\[.*\]\(https?:\/\/.*#(more|contact)\)/i.test(t)) return false; // anchor CTA links
    if (/^\[.*\]\(https?:\/\/(twitter|linkedin|www\.linkedin|x\.com)/i.test(t)) return false; // social share
    if (/^\\\\/.test(t)) return false; // escaped backslash lines
    if (t === "Share") return false;
    // Short standalone label between icons (metadata like "Biotech company", "Clinical")
    if (t.length < 80 && !t.startsWith("## ") && !t.startsWith("**")) {
      // Check if nearby lines (within 3) are icon images
      const nearby = rawSummaryLines.slice(Math.max(0, i - 3), i).concat(rawSummaryLines.slice(i + 1, i + 3));
      const hasNearbyIcon = nearby.some((l) => /^!\[/.test(l.trim()));
      if (hasNearbyIcon) return false;
    }
    return true;
  });

  const summary = cleanSummaryLines.join("\n").trim();

  // Extract each section's content
  const sections: Record<string, string> = {};
  for (let s = 0; s < sectionStarts.length; s++) {
    const start = sectionStarts[s].index + 1; // skip heading/marker line
    const end = s + 1 < sectionStarts.length ? sectionStarts[s + 1].index : lines.length;
    // Strip section marker images (![Challenge], ![Approach], ![Result/Results]) from content
    const sectionLines = lines.slice(start, end).filter((l) => {
      const t = l.trim();
      return !/^!\[(Challenge|Approach|Results?)\]/i.test(t);
    });
    sections[sectionStarts[s].type] = sectionLines.join("\n").trim();
  }

  // Extract testimonial quote from result section
  // Supports: ### "quote" pattern, blockquote, or #### long-form text (paragraph-like heading)
  let quote: string | undefined;
  let quoteAttribution: string | undefined;
  const resultContent = sections.result || "";
  const resultLines = resultContent.split("\n");

  // Pattern 3: blockquote ("> ...") possibly multi-line, optionally ending with "> — Attribution"
  for (let i = 0; i < resultLines.length; i++) {
    const t = resultLines[i].trim();
    if (!/^>\s?/.test(t)) continue;
    const quoteLines: string[] = [];
    let j = i;
    for (; j < resultLines.length; j++) {
      const lt = resultLines[j].trim();
      if (!/^>\s?/.test(lt)) break;
      quoteLines.push(lt.replace(/^>\s?/, ""));
    }
    // Detect attribution as the last line starting with — or --
    let attribution: string | undefined;
    const last = quoteLines[quoteLines.length - 1]?.trim() ?? "";
    if (/^(—|--|–)\s*/.test(last)) {
      attribution = last.replace(/^(—|--|–)\s*/, "").trim();
      quoteLines.pop();
    }
    quote = quoteLines.join(" ").replace(/\s+/g, " ").trim();
    if (attribution) quoteAttribution = attribution;
    sections.result = resultLines.slice(0, i).concat(resultLines.slice(j)).join("\n").trim();
    break;
  }

  for (let i = 0; i < resultLines.length && !quote; i++) {
    const trimmed = resultLines[i].trim();
    // Pattern 1: ### "quote text..." or #### "quote text..."
    const quotedMatch = trimmed.match(/^#{3,4}\s*[""\u201C](.+?)[""\u201D]*$/);
    if (quotedMatch) {
      quote = quotedMatch[1].trim();
      // Next non-empty line should be attribution
      for (let j = i + 1; j < resultLines.length; j++) {
        const attrLine = resultLines[j].trim();
        if (attrLine && !attrLine.startsWith("!")) {
          quoteAttribution = attrLine.replace(/^#{1,4}\s*/, "").trim();
          sections.result = resultLines.slice(0, i).join("\n").trim();
          break;
        }
      }
      break;
    }
    // Pattern 2: #### long paragraph text (>80 chars, likely a quote rendered as heading)
    const longHeadingMatch = trimmed.match(/^####\s+(.{80,})$/);
    if (longHeadingMatch) {
      quote = longHeadingMatch[1]
        .replace(/\\?\|/g, "|")
        .replace(/^[""\u201C]+/, "")
        .replace(/[""\u201D]+$/, "")
        .trim();
      // Look for attribution in following lines
      for (let j = i + 1; j < resultLines.length; j++) {
        const attrLine = resultLines[j].trim();
        if (!attrLine) continue;
        if (attrLine.startsWith("!")) continue;
        // Attribution: #### Name or plain text name
        const attrText = attrLine.replace(/^#{1,4}\s*/, "").trim();
        if (attrText.length < 100) {
          // Check for a second line (title/role)
          let fullAttribution = attrText;
          const nextLine = resultLines[j + 1]?.trim();
          if (nextLine && !nextLine.startsWith("!") && !nextLine.startsWith("#") && nextLine.length < 100) {
            fullAttribution += " — " + nextLine;
            sections.result = resultLines.slice(0, i).join("\n").trim();
          } else {
            sections.result = resultLines.slice(0, i).join("\n").trim();
          }
          quoteAttribution = fullAttribution;
          break;
        }
      }
      break;
    }
  }

  return {
    summary,
    challenge: sections.challenge || "",
    approach: sections.approach || "",
    result: sections.result || "",
    quote,
    quoteAttribution,
  };
}
